import React, { useState, useMemo, useEffect } from "react";
import { Tabs, TabsContent } from "../../../../../ui/tabs";
import SummaryTab from "./SummaryTab";
import ScorecardsTab from "./ScorecardsTab";
import EvaluationTab from "./EvaluationTab";

export default function SessionRightPanel({ summary, scorecards, evaluation, transcript }) {
  const hasSummary = useMemo(() => {
    if (!summary) return false;
    return (
      summary.talk_listen_ratio !== undefined ||
      summary.filler_words !== undefined ||
      summary.talk_speed_wpm !== undefined ||
      summary.longest_monologue !== undefined ||
      (summary.call_takeaways && summary.call_takeaways.length > 0) ||
      (summary.strengths && summary.strengths.length > 0) ||
      (summary.needs_improvement && summary.needs_improvement.length > 0)
    );
  }, [summary]);

  const hasScorecards = useMemo(() => {
    if (!scorecards) return false;
    return (
      scorecards.total_score !== undefined ||
      (scorecards.positive_highlights && scorecards.positive_highlights.length > 0) ||
      (scorecards.areas_for_improvement && scorecards.areas_for_improvement.length > 0) ||
      (scorecards.scorecards && scorecards.scorecards.length > 0)
    );
  }, [scorecards]);

  const hasEvaluation = useMemo(() => {
    return evaluation && Array.isArray(evaluation) && evaluation.length > 0;
  }, [evaluation]);

  const availableTabs = useMemo(() => {
    const tabs = [];
    if (hasSummary) tabs.push({ value: "summary", label: "Summary" });
    if (hasScorecards) tabs.push({ value: "scorecards", label: "Scorecards" });
    if (hasEvaluation) tabs.push({ value: "evaluation", label: "Evaluation" });
    return tabs;
  }, [hasSummary, hasScorecards, hasEvaluation]);

  if (availableTabs.length === 0) {
    return null;
  }

  const defaultTab = useMemo(() => {
    if (hasScorecards) return "scorecards";
    return availableTabs[0]?.value || "summary";
  }, [hasScorecards, availableTabs]);

  const [activeTab, setActiveTab] = useState(defaultTab);

  useEffect(() => {
    const isCurrentTabAvailable = availableTabs.some((tab) => tab.value === activeTab);
    if (!isCurrentTabAvailable && availableTabs.length > 0) {
      setActiveTab(defaultTab);
    }
  }, [availableTabs, activeTab, defaultTab]);

  const currentActiveTab = availableTabs.some((tab) => tab.value === activeTab) ? activeTab : defaultTab;

  return (
    <div className="bg-white  dark:bg-[#0C111D] border border-[#EAECF0] dark:border-[#1F242F] rounded-lg flex flex-col ">
      <Tabs value={currentActiveTab} onValueChange={setActiveTab} className="h-full flex flex-col">
        {/* Only show tabs if more than one */}
        {availableTabs.length > 1 && (
          <div className="border-b border-[#EAECF0] dark:border-[#1F242F] bg-white dark:bg-[#0C111D] px-3 pt-3 flex-shrink-0">
            <nav className="-mb-px flex space-x-8">
              {availableTabs.map((tab) => (
                <button
                  key={tab.value}
                  onClick={() => setActiveTab(tab.value)}
                  className={`py-2 px-0 border-b-2 font-semibold text-sm ${
                    currentActiveTab === tab.value
                      ? "border-[#6941C6] dark:border-[#CECFD2] text-[#6941C6] dark:text-[#CECFD2]"
                      : "border-transparent text-[#717680] dark:text-[#94979C] hover:text-[#717680] hover:border-gray-300 dark:hover:text-gray-300"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </nav>
          </div>
        )}

        {hasSummary && (
          <TabsContent value="summary" className="flex-1 overflow-y-auto m-0 p-6">
            <SummaryTab metrics={summary} />
          </TabsContent>
        )}

        {hasScorecards && (
          <TabsContent value="scorecards" className="flex-1 overflow-y-auto m-0 p-6">
            <ScorecardsTab report={scorecards} transcript={transcript} />
          </TabsContent>
        )}

        {hasEvaluation && (
          <TabsContent value="evaluation" className="flex-1 overflow-y-auto m-0 p-6">
            <EvaluationTab criteria={evaluation} />
          </TabsContent>
        )}
      </Tabs>
    </div>
  );
}
